import { ActionMenu, Card, Icon, Pills, formatDate, type OptionProps } from '@amelio/ui-library';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import DeleteObjectiveDialog from './DeleteObjectiveDialog';
import KeyResultsPanel from './KeyResultsPanel';
import ObjectiveContributors from './ObjectiveContributors';
import ProgressRingColumn from './ProgressRingColumn';

import type { User } from '@/types/directory/User';
import type { ObjectiveOrDraft } from '@/types/objectives';

import { TileLink } from '@/components/TileLink';
import { ObjectiveType } from '@/constants/ObjectiveType';
import { useObjectiveAbilities } from '@/hooks/objectives/useObjectiveAbilities';
import { useNavigate } from '@/hooks/utils/useNavigate';
import {
    objectiveDetailPath,
    objectiveEditPath,
    stepBackState,
} from '@/navigation/objectivesRoutes';
import i18n from '@/plugins/i18n';
import { htmlToPlainText } from '@/utils/htmlSanitizer';
import { getObjectiveStatusOrDraft, opensOnItsForm } from '@/utils/objectiveStatus';

interface ObjectiveCardProps {
    objective: ObjectiveOrDraft;
    employeesById: Map<string, User>;
    /** No menu at all, whatever the reader may do: the tile only opens the objective. */
    isReadOnly?: boolean;
}

export default function ObjectiveCard({
    objective,
    employeesById,
    isReadOnly = false,
}: ObjectiveCardProps) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    // The card is also read on the performance profile, which carries no permission guard: the
    // menu offers only what the reader may do, since offering a refusal is worse than offering nothing.
    const { canDelete } = useObjectiveAbilities();
    const isDeletable = canDelete(objective);

    const state = getObjectiveStatusOrDraft(objective);
    const isDraft = state === 'draft';
    // The draft pill below says it on its own; the state pills only speak for published objectives.
    const status = isDraft ? undefined : state;
    const overdue = status === 'overdue';

    const formattedDueDate = objective.endDate
        ? formatDate({ date: objective.endDate, language: i18n.language })
        : undefined;
    const dueDateTooltip =
        formattedDueDate && t('objectives.card.dueDate', { date: formattedDueDate });
    // A draft reads paler as a whole while its "Draft" pill and the controls it holds stay crisp.
    // Its text takes the secondary copy colour, which keeps the AA contrast opacity would lose;
    // only the ring, a graphic, is faded.
    const textColorClassName = isDraft ? 'text-obj-slate' : 'text-navy';
    const ringClassName = isDraft ? 'opacity-60' : '';

    // `useNavigate`, never `history.push`: the performance profile mounts this card inside a
    // MemoryRouter of its own, so a raw push would move an in-memory history the host cannot see
    // and the click would do nothing at all. The hook dispatches to the host when embedded.
    const openPath = opensOnItsForm(objective)
        ? objectiveEditPath(objective.id)
        : objectiveDetailPath(objective.id);

    const editItem: OptionProps = {
        id: 'edit',
        label: t('objectives.card.edit'),
        onClick: () => navigate({ path: objectiveEditPath(objective.id), state: stepBackState() }),
        icon: (
            <Icon
                name={'edit-edit-2'}
                size={'sm'}
                color={'inherit'}
            />
        ),
    };
    const deleteItem: OptionProps = {
        id: 'delete',
        label: t('objectives.card.delete'),
        onClick: () => setIsDeleteDialogOpen(true),
        danger: true,
        icon: (
            <Icon
                name={'essential-trash'}
                size={'sm'}
                color={'inherit'}
            />
        ),
    };
    const menuItems = isReadOnly
        ? []
        : [...(objective.canEdit ? [editItem] : []), ...(isDeletable ? [deleteItem] : [])];

    return (
        // A click anywhere on the card leads to the detail, or to the form of a draft. The keyboard and assistive technology
        // reach it through the title, the card's one link; the menu, the key results toggle and
        // the delete dialog stop their own clicks before they get here.
        <div onClick={() => navigate({ path: openPath, state: stepBackState() })}>
            <Card
                padding={'none'}
                bgColor={'bg-white/70'}
                borderColor={'rgba(255,255,255,0.6)'}
                className={
                    'relative min-h-[176px] cursor-pointer overflow-hidden !rounded-[32px] shadow-[0_8px_40px_-12px_rgba(15,23,42,0.08)] backdrop-blur-[24px] transition-transform hover:scale-[1.01]'
                }
                dataTestId={`objective-card-${objective.id}`}
            >
                <div className={'obj-card-row'}>
                    {/* The graduated ring lives in the left column, the due date right under it. Like
                    the rest of the card, a click here reaches the wrapping div's handler above.
                    Two layouts, one per breakpoint -- only one is ever visible, switched by the
                    plain-CSS `.obj-ring-desktop` / `.obj-ring-mobile` classes (see app.css for
                    why this can't be `hidden md:flex` here). Both layouts render the same
                    `ProgressRingColumn`, sized by its own `variant` prop. */}
                    <ProgressRingColumn
                        variant={'desktop'}
                        progression={objective.progression}
                        overdue={overdue}
                        formattedDueDate={formattedDueDate}
                        progressAriaLabel={t('objectives.card.progressAria', {
                            value: objective.progression,
                        })}
                        dueDateTooltip={dueDateTooltip}
                        dataTestIdPrefix={`objective-card-${objective.id}`}
                        ringClassName={ringClassName}
                    />

                    <ProgressRingColumn
                        variant={'mobile'}
                        progression={objective.progression}
                        overdue={overdue}
                        formattedDueDate={formattedDueDate}
                        progressAriaLabel={t('objectives.card.progressAria', {
                            value: objective.progression,
                        })}
                        dueDateTooltip={dueDateTooltip}
                        dataTestIdPrefix={`objective-card-${objective.id}`}
                        ringClassName={ringClassName}
                    />

                    {/* The whole card is clickable (see the wrapping div above); the title is its
                    real link, inside the heading so it stays a heading for assistive technology. */}
                    <div className={'obj-card-body flex min-w-[240px] flex-1 flex-col'}>
                        <div className={'flex flex-col gap-1.5'}>
                            <div className={'flex flex-wrap items-center gap-3'}>
                                <h3
                                    className={`${textColorClassName} text-[20px] leading-[25px] font-medium`}
                                >
                                    {/* The pill beside the title is visual only: the link
                                        itself says it leads to a draft. */}
                                    <TileLink
                                        to={openPath}
                                        state={stepBackState()}
                                        ariaLabel={
                                            isDraft
                                                ? t('objectives.card.draftLinkLabel', {
                                                      title: objective.title,
                                                  })
                                                : undefined
                                        }
                                        dataTestId={`objective-card-${objective.id}-title`}
                                    >
                                        {objective.title}
                                    </TileLink>
                                </h3>

                                {objective.type === ObjectiveType.TEAM && (
                                    <span
                                        className={`inline-flex flex-shrink-0 items-center gap-1.5 rounded-full bg-gradient-to-r from-[#A78BFA] via-[#A855F7] to-[#6B21A8] px-3 py-1 text-[10px] font-bold tracking-[0.08em] whitespace-nowrap text-white uppercase shadow-sm`}
                                    >
                                        <Icon
                                            name={'users-people'}
                                            size={'xs'}
                                            color={'inherit'}
                                        />

                                        {t('objectives.card.collective')}
                                    </span>
                                )}

                                {/* Where the objective's state would read, since a draft has none. */}
                                {isDraft && (
                                    <span
                                        aria-hidden
                                        className={'flex-shrink-0'}
                                    >
                                        <Pills
                                            variant={'draftLight'}
                                            size={'tinted'}
                                            className={'whitespace-nowrap'}
                                            dataTestId={`objective-card-${objective.id}-draft`}
                                        >
                                            {t('common.draft')}
                                        </Pills>
                                    </span>
                                )}

                                {overdue && (
                                    <span
                                        className={
                                            'bg-error flex-shrink-0 rounded-full px-3 py-1 text-[11px] font-medium whitespace-nowrap text-white'
                                        }
                                    >
                                        {t('objectives.card.overdue')}
                                    </span>
                                )}

                                {/* Amber, between silence and the red of a missed deadline: the
                                    objective can still be caught up. Never shown beside the red
                                    badge -- the two states exclude each other by construction. */}
                                {status === 'atRisk' && (
                                    <span
                                        className={
                                            'bg-warning flex-shrink-0 rounded-full px-3 py-1 text-[11px] font-medium whitespace-nowrap text-white'
                                        }
                                    >
                                        {t('objectives.card.atRisk')}
                                    </span>
                                )}
                            </div>

                            {objective.description && (
                                <p
                                    className={`${textColorClassName} text-[14px] leading-[24px] font-medium`}
                                >
                                    {htmlToPlainText(objective.description, ' ')}
                                </p>
                            )}
                        </div>

                        <ObjectiveContributors
                            objective={objective}
                            employeesById={employeesById}
                            placement={'card'}
                        />
                    </div>

                    {/* Top-right kebab menu: a sibling of the clickable surface, never nested in it.
                        Clicks are stopped here, key presses are not: the menu closes on Escape
                        through a listener on the document, and the card above listens for clicks
                        only, so holding keys back would close nothing and guard nothing. */}
                    {menuItems.length > 0 && (
                        <div
                            onClick={(event) => event.stopPropagation()}
                            className={
                                'absolute flex flex-shrink-0 max-md:top-4 max-md:right-4 md:top-[36px] md:right-[36px]'
                            }
                            data-testid={`objective-card-${objective.id}-menu-wrapper`}
                        >
                            <ActionMenu
                                items={menuItems}
                                ariaLabel={t('objectives.card.moreOptions')}
                                variant={'default'}
                                size={'sm'}
                            />
                        </div>
                    )}
                </div>

                <div onClick={(event) => event.stopPropagation()}>
                    <KeyResultsPanel keyResults={objective.keyResults} />
                </div>

                {/* Mounted only with its menu item: without it nothing can ever open the dialog. */}
                {!isReadOnly && isDeletable && (
                    <DeleteObjectiveDialog
                        isOpen={isDeleteDialogOpen}
                        objective={objective}
                        onCancel={() => setIsDeleteDialogOpen(false)}
                        onSuccess={() => setIsDeleteDialogOpen(false)}
                    />
                )}
            </Card>
        </div>
    );
}
